import type {
  Channel,
  ChannelOverview,
  ChannelReceipt,
  CreateChannelInput,
  DeleteChatScope,
  Message,
  Page,
  PaginationQuery,
} from '@orbit-sync/shared-types';
import { request } from './client.js';

/**
 * `POST /api/v1/channels`.
 *
 * Direct channels are idempotent per pair: asking twice returns the same
 * channel, so "message this person" needs no existence check first.
 */
export function createChannel(input: CreateChannelInput): Promise<Channel> {
  return request<Channel>('/channels', { method: 'POST', body: input });
}

/**
 * `GET /api/v1/channels` - the conversation list: every channel the user sees,
 * with its newest message and unread count. Includes "Saved messages".
 */
export function listChannels(signal?: AbortSignal): Promise<ChannelOverview[]> {
  return request<ChannelOverview[]>('/channels', { signal });
}

/**
 * `GET /api/v1/channels/:channelId/messages` - newest first.
 *
 * `before` is the opaque cursor from a previous page. Pass it back unchanged;
 * its encoding is a server detail.
 */
export function listMessages(
  channelId: string,
  query: Partial<PaginationQuery> = {},
  signal?: AbortSignal,
): Promise<Page<Message>> {
  return request<Page<Message>>(`/channels/${channelId}/messages`, {
    query: { limit: query.limit, before: query.before },
    signal,
  });
}

/**
 * `GET /api/v1/channels/:channelId/receipts` - every member's delivery and read
 * cursors, so a freshly opened channel can draw its ticks at once. Changes after
 * that arrive live as `chat.receipt`.
 */
export function listReceipts(channelId: string, signal?: AbortSignal): Promise<ChannelReceipt[]> {
  return request<ChannelReceipt[]>(`/channels/${channelId}/receipts`, { signal });
}

/**
 * "Delete chat": hidden for the caller (a direct chat, `me`), deleted for both
 * people (`everyone`), left (a group), or emptied ("Saved messages").
 */
export function deleteChat(channelId: string, scope: DeleteChatScope = 'me'): Promise<void> {
  return request<void>(`/channels/${channelId}`, { method: 'DELETE', query: { scope } });
}

export function addMembers(channelId: string, memberIds: string[]): Promise<Channel> {
  return request<Channel>(`/channels/${channelId}/members`, {
    method: 'POST',
    body: { memberIds },
  });
}
